export interface CotizacionEdicionContratanteContexto {
  idCotizacion: number;
  contratante: any;
  pasoActual: number;
  idTipoSolicitud: number | null;
  tieneProgramasContratantePersistidos: boolean;
  programasContratantePersistidos: any[];
  reiniciarProgramasPorCambioPlan?: boolean;
}

export interface CotizacionPrecargadaContexto {
  precargada: boolean;
  editMode?: boolean;
  idCotizacion: number | null;
  idContratante?: number | null;
  esUsuario?: boolean;
  idsValidaciones?: number[];
  tipoDocumento: string;
  numeroDocumento: string;
  pasoActual?: number;
  idTipoSolicitud?: number | null;
  tieneProgramasContratantePersistidos?: boolean;
  programasContratantePersistidos?: any[];
  reiniciarProgramasPorCambioPlan?: boolean;
  fecha: string;
}

export interface ContextoEdicionModalContratante {
  editMode: true;
  pasoActual: number;
  idTipoSolicitud: number | null;
  tieneProgramasContratantePersistidos: boolean;
  programasContratantePersistidos: any[];
  reiniciarProgramasPorCambioPlan?: boolean;
}

function esBeneficiarioContratante(beneficiario: any): boolean {
  const parentesco = (beneficiario?.parentescoId ?? beneficiario?.parentesco_id ?? '')
    .toString()
    .trim()
    .toUpperCase();
  const esContratante = beneficiario?.esContratante ?? beneficiario?.es_contratante;

  return parentesco === 'T'
    || esContratante === true
    || (esContratante ?? '').toString().trim().toUpperCase() === 'S';
}

/** Obtiene los programas persistidos exclusivamente para el titular/contratante. */
export function obtenerProgramasPersistidosContratante(
  beneficiarios: any[] | null | undefined
): any[] {
  if (!Array.isArray(beneficiarios)) return [];

  const contratante = beneficiarios.find(esBeneficiarioContratante);
  if (!contratante) return [];

  const programas = Array.isArray(contratante?.programasConfig?.programas)
    ? contratante.programasConfig.programas
    : contratante?.programas;

  return Array.isArray(programas) ? programas : [];
}

/** Indica si iniciar-edicion devolvió programas persistidos para el titular. */
export function tieneProgramasPersistidosContratante(beneficiarios: any[] | null | undefined): boolean {
  return obtenerProgramasPersistidosContratante(beneficiarios).some((programa: any) =>
    !!(programa?.programa ?? programa?.id ?? programa?.codigo ?? programa?.code ?? programa?.planCodi)
  );
}

/**
 * Actualiza el contexto sin perder las banderas de una edición activa.
 * Si el id cambia, descarta el contexto anterior para no mezclar cotizaciones.
 */
export function combinarContextoCotizacionPrecargada(
  anterior: Partial<CotizacionPrecargadaContexto> | null,
  cambios: Partial<CotizacionPrecargadaContexto>,
  editModeExplicito = false
): CotizacionPrecargadaContexto {
  const idAnterior = Number(anterior?.idCotizacion ?? 0);
  const idNuevo = Number(cambios?.idCotizacion ?? 0);
  const mismoId = !(idAnterior > 0 && idNuevo > 0 && idAnterior !== idNuevo);
  const base = mismoId ? (anterior ?? {}) : {};
  const combinado = { ...base, ...cambios } as CotizacionPrecargadaContexto;

  if (editModeExplicito || (mismoId && anterior?.editMode === true)) {
    combinado.editMode = true;
  } else {
    delete combinado.editMode;
  }

  combinado.precargada = cambios.precargada ?? base.precargada ?? false;
  combinado.idCotizacion = cambios.idCotizacion ?? base.idCotizacion ?? null;
  combinado.tipoDocumento = (cambios.tipoDocumento ?? base.tipoDocumento ?? '').toString();
  combinado.numeroDocumento = (cambios.numeroDocumento ?? base.numeroDocumento ?? '').toString();
  combinado.fecha = cambios.fecha ?? base.fecha ?? new Date().toISOString();

  return combinado;
}
